import { H264_MAX_RESOLUTION, VideoSettings } from '../constants/video-settings';

/** Camera video is captured 16:9 to match the video tiles. */
const CAMERA_ASPECT = 16 / 9;

export function buildCameraConstraints(
  settings: VideoSettings,
  deviceId?: string,
  strict?: boolean
): MediaStreamConstraints['video'] {
  const { resolution, maxFramerate } = settings;
  const constraints: MediaTrackConstraints = {
    frameRate: { ideal: maxFramerate },
    // Browsers that crop and scale (Chrome) then deliver 16:9 even from a 4:3
    // camera mode — including 'Source', which sets no size before the
    // camera's capabilities are known.
    aspectRatio: { ideal: CAMERA_ASPECT },
  };
  if (resolution.width > 0 && resolution.height > 0) {
    constraints.width = strict
      ? { ideal: resolution.width, min: resolution.width }
      : { ideal: resolution.width };
    constraints.height = strict
      ? { ideal: resolution.height, min: resolution.height }
      : { ideal: resolution.height };
  }
  if (deviceId) {
    constraints.deviceId = { exact: deviceId };
  }
  return constraints;
}

/**
 * Constraints for the 'Source' resolution once the camera is open: its
 * largest 16:9 frame. The largest mode itself is often 4:3 or 3:2, which
 * would show side bars in the 16:9 tiles. applyConstraints replaces the whole
 * constraint set, so the frame rate is repeated — without it, the browser
 * may pick a full-sensor mode that only runs at a few frames per second.
 */
export function buildSourceCameraConstraints(
  settings: VideoSettings,
  capabilities: MediaTrackCapabilities
): MediaTrackConstraints | null {
  const maxWidth = capabilities.width?.max;
  const maxHeight = capabilities.height?.max;
  if (!maxWidth || !maxHeight) {
    return null;
  }
  const width = Math.min(maxWidth, Math.round(maxHeight * CAMERA_ASPECT));
  return {
    width: { ideal: width },
    height: { ideal: Math.round(width / CAMERA_ASPECT) },
    aspectRatio: { ideal: CAMERA_ASPECT },
    frameRate: { ideal: settings.maxFramerate },
  };
}

export function buildScreenConstraints(settings: VideoSettings): MediaStreamConstraints['video'] {
  const { resolution, maxFramerate, codec } = settings;
  const constraints: MediaTrackConstraints = {
    frameRate: { ideal: maxFramerate },
  };
  if (resolution.width > 0 && resolution.height > 0) {
    constraints.width = { max: resolution.width };
    constraints.height = { max: resolution.height };
  }
  // Source + H264: cap at the codec's max resolution
  if (resolution.width === 0 && resolution.height === 0 && codec === 'H264') {
    constraints.width = { max: H264_MAX_RESOLUTION.width };
    constraints.height = { max: H264_MAX_RESOLUTION.height };
  }
  return {
    ...constraints,
    displaySurface: 'monitor',
    logicalSurface: true,
    cursor: true,
  } as MediaTrackConstraints;
}
